import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * O campo de assinatura da vistoria (at-64, frente D/D2).
 *
 * 🔴 **O motor é o de `SignatureTermScreen.tsx:33-233`, preservado verbatim.** Só
 * a moldura e a API mudaram: a lógica de traço, escala e redimensionamento é a
 * mesma linha a linha.
 *
 * ## Por que NÃO usa o `react-signature-canvas` do `/assinatura` da adesão
 *
 * Decisão registrada no plano (P5): as duas assinaturas produzem **imagens
 * diferentes para documentos diferentes** — esta vai ao PDF do laudo e ao SGA em
 * `100% × 180px × DPR`; a do contrato é fixa em 300×150. Unificar mudaria a
 * imagem gravada de um dos dois, que é a mesma ressalva que a at-60 fez ao
 * preservar o 300×150.
 *
 * 📌 E este motor é o **melhor** dos dois: é DPR-aware (nítido em tela retina),
 * escuta Pointer Events com `passive: false` (o que permite bloquear o scroll do
 * celular durante o traço), usa `setPointerCapture` (o traço não escapa ao sair
 * da área) e — o que mais importa em campo — **preserva o desenho ao
 * redimensionar**, via `ResizeObserver`: girar o aparelho no meio da assinatura
 * não apaga o que o associado já assinou.
 *
 * ⚠️ **O canvas é montado junto com o diálogo** (`Modal` do kit), então quando
 * este componente monta ele já está visível e `getBoundingClientRect` devolve
 * dimensão real. Era o papel do `showPad` da tela antiga; aqui o próprio mount
 * cumpre isso, e nenhum `setTimeout` é necessário (mesma diferença que a at-63
 * encontrou entre a câmera da vistoria e a de documentos).
 */

export interface ApiCanvasAssinatura {
  /** Apaga o traço. */
  limpar: () => void;
  /** `true` enquanto nada foi desenhado. */
  vazio: () => boolean;
  /** PNG em base64 (`data:image/png;base64,…`), ou `null` se não houver canvas. */
  paraDataUrl: () => string | null;
}

interface PropsCanvasAssinaturaVistoria {
  /** Altura em px da área de assinatura. ⚠️ É medida da IMAGEM gravada. */
  altura?: number;
  /** Rótulo acessível da área — o canvas é `role="img"`. */
  rotuloAcessivel?: string;
}

const CanvasAssinaturaVistoria = React.forwardRef<
  ApiCanvasAssinatura,
  PropsCanvasAssinaturaVistoria
>(({ altura = 180, rotuloAcessivel = 'Área de assinatura' }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const hasInkRef = useRef(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    // Preserva o desenho atual ao redimensionar (ex.: rotação no mobile)
    const prevDataUrl = hasInkRef.current ? canvas.toDataURL('image/png') : null;

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Reset + escala pra desenhar em coordenadas CSS (px) com nitidez em telas retina
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'currentColor';
    ctx.lineWidth = 2.5;

    // Limpa na escala correta
    ctx.clearRect(0, 0, width, height);

    ctxRef.current = ctx;

    if (prevDataUrl) {
      const img = new Image();
      img.onload = () => {
        // Desenha em coordenadas CSS (já com transform aplicado)
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = prevDataUrl;
    }
  }, []);

  const getPointFromEvent = useCallback((e: PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      limpar: () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.clearRect(0, 0, rect.width, rect.height);
        hasInkRef.current = false;
      },
      vazio: () => !hasInkRef.current,
      paraDataUrl: () => canvasRef.current?.toDataURL('image/png') ?? null,
    }),
    [],
  );

  useEffect(() => {
    initCanvas();

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const onPointerDown = (e: PointerEvent) => {
      // Evita scroll/zoom no mobile durante a assinatura
      e.preventDefault();
      if (!ctxRef.current) initCanvas();

      const point = getPointFromEvent(e);
      const ctx = ctxRef.current;
      if (!point || !ctx) return;

      isDrawingRef.current = true;
      lastPointRef.current = point;
      hasInkRef.current = true;

      canvas.setPointerCapture(e.pointerId);

      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
      // Desenha um ponto inicial (tap) de forma consistente
      ctx.lineTo(point.x + 0.01, point.y + 0.01);
      ctx.stroke();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      const point = getPointFromEvent(e);
      const ctx = ctxRef.current;
      if (!point || !ctx) return;

      const last = lastPointRef.current;
      if (!last) {
        lastPointRef.current = point;
        return;
      }

      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      lastPointRef.current = point;
    };

    const endStroke = (e: PointerEvent) => {
      if (!isDrawingRef.current) return;
      e.preventDefault();
      isDrawingRef.current = false;
      lastPointRef.current = null;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    };

    // Listeners com passive:false para permitir preventDefault
    canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
    canvas.addEventListener('pointermove', onPointerMove, { passive: false });
    canvas.addEventListener('pointerup', endStroke, { passive: false });
    canvas.addEventListener('pointercancel', endStroke, { passive: false });

    const onResize = () => initCanvas();
    window.addEventListener('resize', onResize);

    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => initCanvas())
      : null;
    ro?.observe(canvas);

    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', endStroke);
      canvas.removeEventListener('pointercancel', endStroke);
    };
  }, [getPointFromEvent, initCanvas]);

  return (
    /*
     * 🔴 **O campo era CORTADO no mobile, com barra de rolagem horizontal**
     * (relatado por Victor em 10/09/2026). Três decisões de layout aqui existem
     * para isso não voltar a acontecer.
     *
     * A causa está no motor, e é a razão de ele ser preservado verbatim: o
     * `initCanvas` **fixa a largura em PIXELS** (`canvas.style.width = rect.width`),
     * medida uma vez. A partir daí o canvas é rígido — e se o container encolher
     * depois da medição, ele passa a exceder. É o que acontece dentro de um
     * diálogo: o corpo do `Modal` é `overflow-y-auto`, e a barra de rolagem
     * vertical, quando aparece, **rouba ~15px da largura** já medida.
     *
     *   1. **`max-w-full` no canvas** — é a correção que fecha o caso: a largura
     *      fixada em px nunca ultrapassa o container, seja qual for a medida
     *      antiga. Sem ela, qualquer reflow do diálogo volta a cortar o campo;
     *   2. **a moldura no WRAPPER, não no canvas** — tira a borda da conta da
     *      medição (`rect.width` a inclui) e é o desenho da tela antiga, onde ela
     *      vinha do card em volta. O `overflow-hidden` garante que nada vaze;
     *   3. **`block` no canvas** — sem o `preflight`, `<canvas>` é `inline`, e aí
     *      o `line-height` do pai acrescenta uma faixa de alguns pixels embaixo.
     *
     * 📌 **O `box-sizing: border-box` global EXISTE** neste projeto (vem de
     * `src/index.css`, não do `preflight`, que está desligado) — então borda com
     * `width: 100%` não soma. A primeira leitura deste defeito culpou isso, e
     * estava errada; fica registrado para ninguém repetir o diagnóstico.
     */
    <div className="tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-2 tw-border-brand tw-bg-white">
      <canvas
        ref={canvasRef}
        /* ⚠️ Os atributos `width`/`height` são só o tamanho inicial do buffer: o
           `initCanvas` os recalcula pela medida real × DPR no primeiro efeito. */
        width={100}
        height={altura}
        style={{ height: altura, touchAction: 'none' }}
        aria-label={rotuloAcessivel}
        role="img"
        className="tw-block tw-w-full tw-max-w-full tw-text-texto-titulo"
      />
    </div>
  );
});

CanvasAssinaturaVistoria.displayName = 'CanvasAssinaturaVistoria';

export default CanvasAssinaturaVistoria;
